import React, { Fragment, Component } from 'react';
import { HeroSection } from '../components/HeroSection';
import { AnimatedSection, ParallaxLayer } from '../components/AnimatedSection';
import { TextReveal, CountUp, Marquee, ShinyText, GradientText } from '../components/Animations';
import { CardGrid } from '../components/CardGrid';
import { SignalRibbon, IntegrationWall } from '../components/Graphics';
import { Shield, Zap, Target, Activity, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlaceholderGraphic } from '../components/PlaceholderGraphic';
// Decorative Concentric Circles Component
function ConcentricCircles({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`absolute pointer-events-none opacity-20 ${className}`} data-id="element-431">
      <div className="relative w-[800px] h-[800px] flex items-center justify-center" data-id="element-432">
        {[...Array(8)].map((_, i) => <div key={i} className={`absolute rounded-full border ${i === 3 ? 'border-picus-accent border-2 opacity-100' : 'border-gray-600'}`} style={{
        width: `${(i + 1) * 100}px`,
        height: `${(i + 1) * 100}px`
      }} data-id="element-433" />)}
      </div>
    </div>;
}
export function Home() {
  return <div className="pb-20 bg-picus-bg" data-id="element-434">
      <HeroSection eyebrow="Picus Platform" headline="Autonomous exposure validation for teams that need proof, not more noise" subhead="Picus Platform gives security teams real-time, context-rich validation across controls, exposures, and attack paths. It starts with the pain teams already feel today — proving controls work, knowing what to fix, and reducing manual effort — then extends that into autonomous validation with Picus Swarm." primaryCta={{
      label: 'See the Platform',
      to: '/platform'
    }} secondaryCta={{
      label: 'Get a Demo',
      to: '/contact'
    }} graphic={null} centered={true} data-id="element-435" />

      <div className="section-container space-y-32 mt-16" data-id="element-436">
        {/* Section 1 */}
        <section className="relative flex flex-col items-center" data-id="element-437">
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[640px] mb-12" data-id="element-438">
            <h2 className="mb-4 text-white" data-id="element-439">
              99 problems, but finding an issue isn't one.
            </h2>
            <p className="text-picus-muted" data-id="element-440">
              Most teams already have more findings than they can work through.
              The real challenge is proving what works, knowing what matters,
              and acting before the backlog gets worse.
            </p>
          </AnimatedSection>
          <ParallaxLayer speed={0.15} className="relative z-10 w-full" data-id="element-441">
            <CardGrid columns={2} cards={[{
            title: 'Legacy Assessment',
            description: 'Thousands of alerts, disconnected dashboards, and theoretical risk.'
          }, {
            title: 'Validated Action',
            description: 'A short, prioritized list of exposures that truly matter to your business.'
          }]} data-id="element-442" />
          </ParallaxLayer>
        </section>

        {/* Section 2 */}
        <section className="relative flex flex-col items-center" data-id="element-443">
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[640px] mb-12" data-id="element-444">
            <h2 className="mb-4 text-white" data-id="element-445">Prove your controls work.</h2>
            <p className="mb-8 text-picus-muted" data-id="element-446">
              Breach and Attack Simulation is still the fastest path to
              confidence in the stack you already own. Picus BAS helps teams
              validate whether prevention and detection controls are doing what
              they are supposed to do.
            </p>
            <Link to="/breach-and-attack-simulation" className="btn-secondary" data-id="element-447">
              Explore BAS
            </Link>
          </AnimatedSection>
          <ParallaxLayer speed={0.12} className="relative z-10 w-full" data-id="element-448">
            <CardGrid columns={3} cards={[{
            title: 'Network Controls',
            description: 'Validate firewall and IPS effectiveness.'
          }, {
            title: 'Endpoint Controls',
            description: 'Test EDR prevention and telemetry.'
          }, {
            title: 'Detection Rules',
            description: 'Ensure SIEM alerts fire correctly.'
          }]} data-id="element-449" />
          </ParallaxLayer>
        </section>

        {/* Section 3 */}
        <section className="relative flex flex-col items-center" data-id="element-450">
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[640px] mb-12" data-id="element-451">
            <h2 className="mb-4 text-white" data-id="element-452">Reduce the manual Gap.</h2>
            <p className="mb-8 text-picus-muted" data-id="element-453">
              Picus Swarm is the agentic operating layer of Picus Platform. It
              helps teams trigger validation on meaningful change, route the
              right context to the right owner, and reduce manual coordination
              between signal, validation, priority, and action.
            </p>
            <Link to="/platform/picus-swarm" className="btn-secondary" data-id="element-454">
              Explore Picus Swarm
            </Link>
          </AnimatedSection>

          <div className="w-full p-3 border border-picus-border relative z-10" data-id="element-455">
            <div className="bg-picus-surface p-9 flex flex-col md:flex-row items-stretch" data-id="element-456">
              {['Signal', 'Validate', 'Prioritize', 'Act', 'Revalidate'].map((step, i) => <Fragment key={step} data-id="element-457">
                    <ParallaxLayer speed={0.05 + 0.04 * i} className={`flex-1 min-w-[150px] p-10 flex items-center justify-center ${i < 4 ? 'border-r border-picus-border' : ''}`} data-id="element-458">
                      <span className="font-mono text-[14px] leading-[16px] font-medium uppercase tracking-[2px] text-white text-center" data-id="element-459">
                        {step}
                      </span>
                    </ParallaxLayer>
                  </Fragment>)}
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="relative flex flex-col items-center" data-id="element-460">
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[640px] mb-12" data-id="element-461">
            <h2 className="mb-4 text-white" data-id="element-462">
              Built to respond to what changed, not what was scheduled.
            </h2>
            <p className="text-picus-muted" data-id="element-463">
              Static scans and calendar-based testing are too slow for
              environments and threats that change constantly. Picus Platform
              uses signals from your stack to trigger validation when it
              matters.
            </p>
          </AnimatedSection>
          <ParallaxLayer speed={0.15} className="relative z-10 w-full" data-id="element-464">
            <CardGrid columns={2} cards={[{
            title: 'Old Model',
            description: 'Periodic validation. Testing happens quarterly regardless of environment changes.'
          }, {
            title: 'New Model',
            description: 'Signal-driven validation triggered by threat, asset, policy, and control changes.'
          }]} data-id="element-465" />
          </ParallaxLayer>
        </section>

        {/* Section 5 */}
        <section className="relative border-y border-picus-border" data-id="element-466">
          <div className="flex flex-col md:flex-row" data-id="element-467">
            <AnimatedSection className="relative z-10 flex-1 p-12 md:p-32 border-b md:border-b-0 md:border-r border-picus-border flex flex-col justify-center" data-id="element-468">
              <h2 className="mb-6 text-white text-left" data-id="element-469">
                Your stack already has the signals.
              </h2>
              <p className="mb-12 text-picus-muted text-left" data-id="element-470">
                Picus gets context from the tools, controls, detections,
                policies, assets, and workflows already running in your
                environment. That context is what makes autonomous validation
                useful instead of generic.
              </p>
              <Link to="/integrations" className="btn-secondary self-start" data-id="element-471">
                See Integrations
              </Link>
            </AnimatedSection>
            <AnimatedSection className="relative z-10 flex-1 p-16 flex items-center justify-center bg-picus-surface" data-id="element-472">
              <IntegrationWall data-id="element-473" />
            </AnimatedSection>
          </div>
        </section>

        {/* Section 6 */}
        <section className="relative flex flex-col items-center border-t border-picus-border pt-32" data-id="element-474">
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[640px] mb-12" data-id="element-475">
            <h2 className="mb-4 text-white" data-id="element-476">
              See how the market responds to real threats.
            </h2>
            <p className="mb-8 text-picus-muted" data-id="element-477">
              Picus Research turns validation data into practical intelligence
              for operators, leaders, and buyers. Explore the Threat Response
              Index, Red Report, Blue Report, and Picus Labs.
            </p>
            <Link to="/research" className="btn-secondary" data-id="element-478">
              Explore Research
            </Link>
          </AnimatedSection>
          <ParallaxLayer speed={0.1} className="relative z-10 w-full" data-id="element-479">
            <CardGrid columns={4} cards={[{
            title: 'Threat Response Index',
            to: '/research/threat-response-index',
            tag: 'Featured'
          }, {
            title: 'Red Report',
            to: '/research/red-report'
          }, {
            title: 'Blue Report',
            to: '/research/blue-report'
          }, {
            title: 'Picus Labs',
            to: '/research/picus-labs'
          }]} data-id="element-480" />
          </ParallaxLayer>
        </section>

        {/* Section 7 */}
        <section className="relative flex flex-col items-center border-t border-picus-border pt-32" data-id="element-481">
          <AnimatedSection className="relative z-10 flex flex-col items-center text-center max-w-[640px] mb-12" data-id="element-482">
            <h2 className="mb-4 text-white" data-id="element-483">
              Built For Teams that need evidence, not guesses.
            </h2>
            <p className="text-picus-muted" data-id="element-484">
              Picus helps security teams optimize defense, improve exposure
              awareness, and scale offensive testing. It is trusted by teams
              that need proof they can defend to peers, leadership, and boards.
            </p>
          </AnimatedSection>

          <div className="w-full p-3 border border-picus-border relative z-10" data-id="element-485">
            <div className="bg-picus-surface p-9 grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-486">
              {[{
              top: '4.9 on G2',
              bottom: '',
              stars: true
            }, {
              top: '4.8 on Gartner peer insights',
              bottom: '',
              stars: true
            }, {
              top: '#1 Innovation Index on Frost Radar 2026',
              bottom: '',
              stars: false
            }, {
              top: '95% Recommendation rate',
              bottom: '',
              stars: false
            }].map((stat, i) => <div key={i} className="p-7 flex flex-col items-center justify-center gap-4 text-center" data-id="element-487">
                  {stat.stars ? <div className="flex gap-1.5 text-[#FBBF24]" data-id="element-488">
                      {[...Array(5)].map((_, j) => <svg key={j} className="w-4 h-4 fill-current" viewBox="0 0 24 24" data-id="element-489">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-490" />
                        </svg>)}
                    </div> : <div className="text-[#FBBF24]" data-id="element-491">
                      <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24" data-id="element-492">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" data-id="element-493" />
                      </svg>
                    </div>}
                  <div className="font-mono text-[14px] leading-[16px] font-medium uppercase tracking-[2px] text-white" data-id="element-494">
                    {stat.top}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Section 8 */}
        <section className="relative border-t border-picus-border overflow-hidden" data-id="element-495">
          <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/patterns/figma-images/ktYrAadlhmchTIJSJQA2an/1-1312.png')] bg-cover bg-center opacity-40 mix-blend-screen" data-id="element-496" />
          <div className="absolute inset-0 bg-gradient-to-r from-picus-bg via-picus-bg/80 to-transparent" data-id="element-497" />

          <AnimatedSection className="relative z-10 py-48 px-12 md:px-24 max-w-[768px]" data-id="element-498">
            <h2 className="text-[48px] leading-[60px] tracking-[5.3px] mb-8 text-white" data-id="element-499">
              Start with the problem you need to solve today
            </h2>
            <p className="mb-12 text-picus-muted text-[16px] leading-[24px]" data-id="element-500">
              Need BAS? Need to know what to fix? Need to reduce the manual gap
              between findings and action? Picus Platform gives you immediate
              value now and a more scalable operating model over time.
            </p>
            <Link to="/contact" className="btn-primary" data-id="element-501">
              Get a Demo
            </Link>
          </AnimatedSection>
        </section>
      </div>
    </div>;
}